import { useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';

import { pushNotificationToStack } from 'Console/data/notification/actions';
import { NOTIFICATION_DURATION } from 'Console/constants/appConstants';

import {
    selectAddonsInstalledListFetchStatus,
    selectAddonsInstalledListReason,
    selectAddonsAvailableListFetchStatus,
    selectAddonsAvailableListReason,
    selectApplicationSettingsUpdateStatus,
    selectAvailableRolloutPoliciesFetchStatus,
    selectAvailableRolloutPoliciesReason,
    selectRolloutPoliciesBySpaceFetchStatus,
    selectRolloutPoliciesBySpaceReason,
} from '../SettingsSelector';

import { REQUEST_STATUS } from '../types';

const FetchInstalledAddonsNotification = (): null => {
    const dispatch = useDispatch();

    const { formatMessage } = useIntl();

    const fetchStatus = useSelector(selectAddonsInstalledListFetchStatus);
    const reason = useSelector(selectAddonsInstalledListReason);

    useEffect(() => {
        switch (fetchStatus) {
            case REQUEST_STATUS.FAILED:
                switch (reason.status) {
                    case 403:
                        dispatch(
                            pushNotificationToStack(formatMessage({ id: 'Settings.accessDenied' }))
                        );
                        break;
                    default:
                }
                break;
            default:
        }
    }, [dispatch, fetchStatus, reason, formatMessage]);

    return null;
};

const FetchAvailableAddonsNotification = (): null => {
    const dispatch = useDispatch();

    const { formatMessage } = useIntl();

    const fetchStatus = useSelector(selectAddonsAvailableListFetchStatus);
    const reason = useSelector(selectAddonsAvailableListReason);

    useEffect(() => {
        switch (fetchStatus) {
            case REQUEST_STATUS.FAILED:
                switch (reason.status) {
                    case 403:
                        dispatch(
                            pushNotificationToStack(formatMessage({ id: 'Settings.accessDenied' }))
                        );
                        break;
                    default:
                }
                break;
            default:
        }
    }, [dispatch, fetchStatus, reason, formatMessage]);

    return null;
};

const RolloutPoliciesByMyOrganizationsNotification = (): null => {
    const dispatch = useDispatch();

    const { formatMessage } = useIntl();

    const fetchStatus = useSelector(selectAvailableRolloutPoliciesFetchStatus);
    const reason = useSelector(selectAvailableRolloutPoliciesReason);

    useEffect(() => {
        switch (fetchStatus) {
            case REQUEST_STATUS.FAILED:
                switch (reason.status) {
                    case 403:
                        dispatch(
                            pushNotificationToStack(formatMessage({ id: 'Settings.accessDenied' }))
                        );
                        break;
                    default:
                        dispatch(
                            pushNotificationToStack(
                                formatMessage({
                                    id: 'Settings.errorGetRolloutPoliciesByMyOrganizations',
                                })
                            )
                        );
                }
                break;
            default:
        }
    }, [dispatch, fetchStatus, reason, formatMessage]);

    return null;
};

const RolloutPoliciesBySpaceNotification = (): null => {
    const dispatch = useDispatch();

    const { formatMessage } = useIntl();

    const fetchStatus = useSelector(selectRolloutPoliciesBySpaceFetchStatus);
    const reason = useSelector(selectRolloutPoliciesBySpaceReason);

    useEffect(() => {
        switch (fetchStatus) {
            case REQUEST_STATUS.FAILED:
                switch (reason.status) {
                    case 403:
                        dispatch(
                            pushNotificationToStack(formatMessage({ id: 'Settings.accessDenied' }))
                        );
                        break;
                    default:
                        dispatch(
                            pushNotificationToStack(
                                formatMessage({ id: 'Settings.errorGetRolloutPoliciesBySpaceId' })
                            )
                        );
                }
                break;
            default:
        }
    }, [dispatch, fetchStatus, reason, formatMessage]);

    return null;
};

const StatusNotification = ({ open, message }) => {
    const [isOpen, setIsOpen] = useState(open);

    const handleOnClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);
    return (
        <Snackbar
            open={isOpen}
            onClose={handleOnClose}
            autoHideDuration={NOTIFICATION_DURATION}
            message={message}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            action={
                <IconButton aria-label="Close" color="inherit" onClick={handleOnClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            }
        />
    );
};

const UpdateStatusNotification = () => {
    const updateStatus = useSelector(selectApplicationSettingsUpdateStatus);

    return (
        <>
            <StatusNotification
                message={<FormattedMessage id="Notification.save" />}
                open={updateStatus === REQUEST_STATUS.SUCCESS}
            />
            <StatusNotification
                message={<FormattedMessage id="Notification.fail" />}
                open={updateStatus === REQUEST_STATUS.FAILED}
            />
        </>
    );
};

const SettingsNotificationContainer = () => (
    <>
        <UpdateStatusNotification />
        <FetchInstalledAddonsNotification />
        <FetchAvailableAddonsNotification />
        <RolloutPoliciesByMyOrganizationsNotification />
        <RolloutPoliciesBySpaceNotification />
    </>
);

export default SettingsNotificationContainer;
